import { BaseModal } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import type { Equipamento } from '../../../../models';
import { normalizarPatrimonio } from '../../../../models';
import { useEquipamentoMutations } from '../../../../hooks/data';
import { TIPO_ATIVO_LABELS } from './ativos.definitions';
import { MOTIVO_EXCLUSAO_ATIVO_LABELS } from './ativos.utils';

interface AtivosExcluidosModalProps {
  isOpen: boolean;
  onClose: () => void;
  excluidos: Equipamento[];
  /** Abre a ficha (só consulta: dados, histórico, anexos). */
  onAbrir: (id: string) => void;
}

function quando(equipamento: Equipamento): string {
  const data = equipamento.excluidoEm?.toDate();
  return data ? data.toLocaleDateString('pt-BR') : '';
}

/** Ativos excluídos (exclusão lógica) — ver quem/quando/por quê e restaurar se foi engano. */
export default function AtivosExcluidosModal({ isOpen, onClose, excluidos, onAbrir }: AtivosExcluidosModalProps) {
  const { user } = useAuth();
  const { restaurarAtivo } = useEquipamentoMutations();
  const ator = user ? { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '', avatar: null } : null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={`Ativos excluídos (${excluidos.length})`} maxWidth="max-w-2xl" fullHeight={false}>
      {excluidos.length === 0 ? (
        <p className="py-6 text-sm text-gray-500">Nenhum ativo excluído.</p>
      ) : (
        <ul className="space-y-2 py-5">
          {excluidos.map((e) => {
            const descricao = [TIPO_ATIVO_LABELS[e.tipo] ?? e.tipo, [e.fabricante, e.modelo].filter(Boolean).join(' ')]
              .filter(Boolean)
              .join(' · ');
            return (
              <li key={e.id}>
                {/* Card inteiro abre a ficha (só consulta); "Restaurar" não propaga o clique. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onAbrir(e.id)}
                  onKeyDown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && onAbrir(e.id)}
                  title="Ver ficha e histórico"
                  className="flex w-full cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-gray-200 bg-white px-4 py-3 text-left hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 font-montserrat text-gray-900">
                      <span className="font-bold">{normalizarPatrimonio(e.patrimonio) ?? 'Sem patrimônio'}</span>
                      {descricao && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="truncate text-sm text-gray-600">{descricao}</span>
                        </>
                      )}
                      {e.nSerie && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm text-gray-600">{e.nSerie}</span>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {e.motivoExclusaoTipo ? MOTIVO_EXCLUSAO_ATIVO_LABELS[e.motivoExclusaoTipo] : 'Excluído'}
                      {e.motivoExclusao ? ` — ${e.motivoExclusao}` : ''}
                      {e.excluidoPor?.name ? ` · por ${e.excluidoPor.name}` : ''}
                      {quando(e) ? ` em ${quando(e)}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">Excluído</span>
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={!ator || restaurarAtivo.isPending}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (ator) restaurarAtivo.mutate({ id: e.id, ator });
                      }}
                      onKeyDown={(ev) => ev.stopPropagation()}
                    >
                      Restaurar
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </BaseModal>
  );
}
